<template>
  <v-sheet class="px-6 py-4" elevation="2" rounded>
    <v-row class="pt-4 mb-2" align="center">
      <v-col cols="7">
        <p
          data-testid="home-text-teamsActivityTitle"
          class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 font-weight-700"
        >
          {{ $t('home.teams_activity.my_teams_activity') }}
        </p>
      </v-col>
      <v-col
        v-if="!isLoading && teams.length > 0"
        cols="5"
        class="mx-0 d-flex justify-end align-center"
      >
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <Button
              v-on="on"
              class="me-2"
              color="grey"
              icon="mdi-menu-down"
              iconPosition="right"
              plain
              data-testid="home-button-teamsActivitySelect"
              :text="selectedTeam.name || $t('home.teams_activity.select_team')"
              truncate
              :maxWidth="200"
            />
          </template>
          <v-list dense>
            <v-list-item
              v-for="team in teams"
              :key="team.name"
              :class="{ active: selectedTeam.name === team.name }"
              @click="selectTeam(team)"
            >
              <span
                class="caption blue-grey--text text--darken-2 text-truncate"
                :style="{ 'max-width': '200px' }"
                >{{ team.name }}</span
              >
            </v-list-item>
          </v-list>
        </v-menu>

        <Button
          color="blue"
          iconPosition="right"
          plain
          size="small"
          data-testid="home-button-teamsActivityViewMore"
          :text="$t('common.button.view_more')"
          @click="goToTeamDetails"
        />
      </v-col>
    </v-row>

    <template v-if="!isLoading && teams.length > 0">
      <v-row no-gutters>
        <v-col cols="8">
          <v-tabs
            v-model="selectedTab"
            slider-size="4"
            slider-color="blue"
            style="z-index: 1; position: relative;"
          >
            <v-tab
              active-class="blue-grey--text text--darken-3"
              class="blue-grey--text text--darken-1"
              key="revsTab"
              @click="selectRevsTab"
              >{{ $t('common.rev_other') }}</v-tab
            >
            <template v-if="hasRoutinesFeatureEnabled">
              <v-tab
                active-class="blue-grey--text text--darken-3"
                class="blue-grey--text text--darken-1"
                key="findingsTab"
                @click="selectFindingsTab"
                >{{ $t('assignments.finding_one') }}</v-tab
              >
              <v-tab
                active-class="blue-grey--text text--darken-3"
                class="blue-grey--text text--darken-1"
                key="routinesTab"
                @click="selectRoutinesTab"
                >{{ $t('routines.routine_other') }}</v-tab
              >
            </template>
          </v-tabs>
        </v-col>
      </v-row>
      <v-divider></v-divider>

      <v-row class="mt-n16">
        <v-col cols="12">
          <v-window v-model="selectedTab">
            <v-window-item
              active-class="tab-content hide-scrollbar"
              key="revsTab"
            >
              <RevsTab
                :teamId="selectedTeam.id"
                :columnsLimit="LIMIT_ACTIVE_COLUMNS"
              />
            </v-window-item>
            <template v-if="hasRoutinesFeatureEnabled">
              <v-window-item
                active-class="tab-content-summary"
                key="findingsTab"
              >
                <FindingsTab
                  :teamId="selectedTeam.id"
                  :columnsLimit="LIMIT_ACTIVE_COLUMNS"
                />
              </v-window-item>

              <v-window-item
                active-class="tab-content hide-scrollbar"
                key="routinesTab"
              >
                <RoutinesTab :teamId="selectedTeam.id" :columnsLimit="5" />
              </v-window-item>
            </template>
          </v-window>
        </v-col>
      </v-row>
    </template>

    <TeamActivitySkeleton v-if="isLoading" />

    <TeamActivityEmptyState v-if="!isLoading && teams.length === 0" />
  </v-sheet>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button'
import RevsTab from '@/components/TeamActivityTables/RevsTab.vue'
import FindingsTab from '@/components/TeamActivityTables/FindingsTab.vue'
import RoutinesTab from '@/components/TeamActivityTables/RoutinesTab.vue'
import TeamActivityEmptyState from '@/components/TeamActivityTables/TeamActivityEmptyState.vue'

import TeamActivitySkeleton from './TeamActivitySkeleton'

const LIMIT_ACTIVE_COLUMNS = 5

export default {
  name: 'TeamActivity',

  mixins: [mixin, trackingMixin],

  components: {
    Button,
    RevsTab,
    FindingsTab,
    RoutinesTab,
    TeamActivitySkeleton,
    TeamActivityEmptyState,
  },

  data() {
    return {
      selectedTab: 'routinesTab',
      selectedTeam: {},
      teams: [],
      isLoading: true,
      LIMIT_ACTIVE_COLUMNS,
    }
  },

  created() {
    this.fetchTeams()
  },

  computed: {
    ...mapGetters(['account', 'organizationId', 'organization']),

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },
  },

  methods: {
    async fetchTeams() {
      try {
        this.isLoading = true
        const response = await api.teams.getTeams(this.organizationId, {
          filters: {
            member: this.account._id,
          },
        })
        this.teams = response.data.results ?? []
        this.selectedTeam = this.teams?.length > 0 ? this.teams[0] : {}
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    selectTeam(team) {
      this.trackHomeNavigate({
        section: 'teamActivity',
        element: 'teamSelector',
        selectedTeam: { name: team.name, id: team.id },
      })
      this.selectedTeam = team
    },

    selectRoutinesTab() {
      this.trackHomeNavigate({
        section: 'teamActivity',
        element: 'routinesTab',
      })
    },

    selectFindingsTab() {
      this.trackHomeNavigate({
        section: 'teamActivity',
        element: 'findingsTab',
      })
    },

    selectRevsTab() {
      this.trackHomeNavigate({
        section: 'teamActivity',
        element: 'revsTab',
      })
    },

    goToTeamDetails() {
      this.trackHomeNavigate({ origin: 'home', destination: 'teamDetails' })
      this.navigateTo({
        name: 'teamDetails',
        params: { teamId: this.selectedTeam.id },
      })
    },
  },
}
</script>

<style scoped lang="scss">
.active {
  background: $color-blue-grey-lighten3;
}
</style>
